import React from "react";
import { Link } from "react-router-dom";
import { photoUrl } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { BedDouble, Bath, Ruler, MapPin } from "lucide-react";

const FALLBACKS = {
  villa: "https://images.unsplash.com/photo-1776619316276-b1b461af9f15?crop=entropy&cs=srgb&fm=jpg&w=940",
  casa: "https://images.unsplash.com/photo-1776619316276-b1b461af9f15?crop=entropy&cs=srgb&fm=jpg&w=940",
  apartamento: "https://images.pexels.com/photos/11296142/pexels-photo-11296142.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  terreno: "https://images.pexels.com/photos/36422828/pexels-photo-36422828.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  local: "https://images.pexels.com/photos/11296142/pexels-photo-11296142.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  oficina: "https://images.pexels.com/photos/11296142/pexels-photo-11296142.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function PropertyCard({ property, index = 0 }) {
  const { t, formatPrice } = useLocale();
  const first = property.photos?.[0];
  const src = first ? photoUrl(first) : FALLBACKS[property.property_type] || FALLBACKS.casa;

  return (
    <Link
      to={`/properties/${property.property_id}`}
      data-testid={`property-card-${property.property_id}`}
      className="prop-card group block animate-fade-up"
      style={{ animationDelay: `${(index % 6) * 60}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted rounded-sm">
        <img
          src={src}
          alt={property.title}
          loading="lazy"
          className="prop-card-img h-full w-full object-cover"
        />
        <div className="absolute top-3 left-3 label-eyebrow bg-background/85 backdrop-blur px-2 py-1 rounded-sm">
          {t(`types.${property.property_type}`)}
        </div>
        {property.transaction_type === "alquiler" && (
          <div className="absolute top-3 right-3 label-eyebrow bg-secondary text-secondary-foreground px-2 py-1 rounded-sm">
            {t("filters.rent")}
          </div>
        )}
      </div>
      <div className="pt-4 pb-2 space-y-2">
        <h3 className="font-serif text-2xl leading-tight group-hover:text-primary transition-colors">
          {property.title}
        </h3>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span className="truncate">{property.address}</span>
        </div>
        <div className="flex items-center gap-5 pt-1 text-sm text-foreground/80">
          <span className="inline-flex items-center gap-1.5">
            <BedDouble className="h-4 w-4" /> {property.bedrooms}
          </span>
          {property.bathrooms > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Bath className="h-4 w-4" /> {property.bathrooms}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <Ruler className="h-4 w-4" /> {property.square_meters} m²
          </span>
        </div>
        <div className="flex items-baseline justify-between pt-2">
          <span className="font-serif text-2xl text-primary">
            {formatPrice(property.price, property.currency)}
          </span>
          {property.transaction_type === "alquiler" && (
            <span className="text-xs text-muted-foreground">{t("common.per_month")}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
